import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Truck, Plus, Edit2, Trash2, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { FormErrorBanner } from '../shared/FormErrorBanner';
import type { CargoQuoteRequest } from '../../types/quotes';
import type {
  AdminQuoteResponseItem,
  ResponseFieldErrors,
  ResponseFormState,
} from '../../hooks/useAdminQuoteDetail';
import { createEmptyResponseFieldErrors, DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from '../../hooks/useAdminQuoteDetail';
import { formatPrice } from '../../lib/currency';

interface QuoteResponsesSectionProps {
  quote: CargoQuoteRequest;
  transportationCompanies: any[];
  responseForm: ResponseFormState;
  setResponseForm: React.Dispatch<React.SetStateAction<ResponseFormState>>;
  fieldErrors: ResponseFieldErrors;
  setFieldErrors: React.Dispatch<React.SetStateAction<ResponseFieldErrors>>;
  submittingResponse: boolean;
  editingResponseId: number | null;
  setEditingResponseId: (id: number | null) => void;
  formErrors: string[];
  setFormErrors: (errors: string[]) => void;
  markingContactedId: number | null;
  isResponseModalOpen: boolean;
  setIsResponseModalOpen: (open: boolean) => void;
  handleEditResponse: (response: AdminQuoteResponseItem) => void;
  handleDeleteResponse: (responseId: number) => Promise<void>;
  handleMarkContacted: (responseId: number) => Promise<void>;
  handleSubmitResponse: () => Promise<boolean>;
}

export const QuoteResponsesSection: React.FC<QuoteResponsesSectionProps> = ({
  quote,
  transportationCompanies,
  responseForm,
  setResponseForm,
  fieldErrors,
  setFieldErrors,
  submittingResponse,
  editingResponseId,
  setEditingResponseId,
  formErrors,
  setFormErrors,
  markingContactedId,
  isResponseModalOpen,
  setIsResponseModalOpen,
  handleEditResponse,
  handleDeleteResponse,
  handleMarkContacted,
  handleSubmitResponse,
}) => {
  const { t } = useTranslation();

  // ---- UI wrappers that add confirmation dialogs / alerts ----------------

  const onDeleteResponse = async (responseId: number) => {
    if (!window.confirm(t('admin.quote.confirm_delete_response'))) {
      return;
    }
    try {
      await handleDeleteResponse(responseId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.quote.delete_response_failed'));
    }
  };

  const onSubmitResponse = async () => {
    await handleSubmitResponse();
  };

  // Clear the inline price error as the operator corrects the value, so a stale
  // "invalid price" message does not linger until the next submit.
  const onPriceChange = (value: string) => {
    setResponseForm({ ...responseForm, price: value });
    if (fieldErrors.price.length > 0) {
      setFieldErrors((prev) => ({ ...prev, price: [] }));
    }
  };

  const responseItems = quote.quote_responses as unknown as AdminQuoteResponseItem[];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-green-600" />
            {t('admin.quote.responses_title', { count: responseItems.length })} ({responseItems.length})
          </CardTitle>
          <Button size="sm" onClick={() => {
            setEditingResponseId(null);
            setFieldErrors(createEmptyResponseFieldErrors());
            setFormErrors([]);
            setResponseForm({
              transportation_company_id: '',
              price: '',
              currency: DEFAULT_CURRENCY,
              estimated_delivery_date: '',
              loading_date: '',
              no_specific_loading_date: false,
              valid_until: '',
              observations: '',
            });
            setIsResponseModalOpen(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.quote.add_response')}
          </Button>
        </CardHeader>
        <CardContent>
          {responseItems.length === 0 ? (
            <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
              {t('admin.quote.no_responses')}
            </div>
          ) : (
            <div className="space-y-4">
              {responseItems.map((response) => (
                <div key={response.id} className="border rounded-lg p-4 bg-white shadow-sm flex flex-col sm:flex-row justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="text-lg">{response.company_name}</h4>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {t('admin.quote.price_value', { price: formatPrice(response.price, response.currency) })}
                      </Badge>
                      {(response as any).contact_requested_at && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          {t('admin.quote.contact_requested')}
                          {' · '}
                          {formatDistanceToNow(new Date((response as any).contact_requested_at), { addSuffix: true })}
                        </Badge>
                      )}
                      {(response as any).contacted_at && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          {t('admin.quote.contacted')} · {formatDistanceToNow(new Date((response as any).contacted_at), { addSuffix: true })}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 space-y-1">
                      {response.loading_date && (
                        <p>
                          <span className="font-medium">{t('admin.quote.loading_date')}:</span>{' '}
                          {t('admin.quote.date_value', { date: response.loading_date })}
                        </p>
                      )}
                      {response.estimated_delivery_date && (
                        <p>
                          <span className="font-medium">{t('admin.quote.estimated_delivery_short')}:</span>{' '}
                          {t('admin.quote.date_value', { date: response.estimated_delivery_date })}
                        </p>
                      )}
                      {!response.loading_date && !response.estimated_delivery_date && (
                        <p className="text-slate-500 italic">
                          {t('admin.quote.no_specific_loading_date')}
                        </p>
                      )}
                      {response.valid_until && (
                        <p>
                          <span className="font-medium">{t('admin.quote.valid_until_label')}:</span>{' '}
                          {t('admin.quote.date_value', { date: response.valid_until })}
                        </p>
                      )}
                      {response.observations && (
                        <p className="text-slate-500 italic mt-2">
                          {t('admin.quote.observations_value', { value: response.observations })}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {t('admin.quote.added_by', {
                        name: response.created_by,
                        time: formatDistanceToNow(new Date(response.created_at), { addSuffix: true })
                      })}
                    </p>
                  </div>
                  <div className="flex flex-row sm:flex-col gap-2 justify-start sm:justify-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-blue-600"
                      title={t('admin.quote.edit_response')}
                      onClick={() => handleEditResponse(response)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {(response as any).contact_requested_at && !(response as any).contacted_at && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-emerald-600"
                        title={t('admin.quote.mark_contacted')}
                        onClick={() => handleMarkContacted(response.id)}
                        disabled={markingContactedId === response.id}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600"
                      onClick={() => onDeleteResponse(response.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isResponseModalOpen} onOpenChange={(open) => {
        if (!open) {
          setIsResponseModalOpen(false);
          setEditingResponseId(null);
          setFieldErrors(createEmptyResponseFieldErrors());
          setFormErrors([]);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingResponseId
              ? t('admin.quote.edit_response_title')
              : t('admin.quote.add_response_title')}</DialogTitle>
          </DialogHeader>

          <FormErrorBanner errors={formErrors} />

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="company" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {t('admin.quote.company')}
              </label>
              <select
                id="company"
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={responseForm.transportation_company_id}
                onChange={(e) => setResponseForm({...responseForm, transportation_company_id: e.target.value})}
              >
                <option value="">{t('admin.quote.select_company')}</option>
                {transportationCompanies.map((company) => (
                  <option key={company.id} value={String(company.id)}>
                    {company.name}
                  </option>
                ))}
              </select>
              {fieldErrors.transportation_company_id.length > 0 && (
                <div className="space-y-1">
                  {fieldErrors.transportation_company_id.map((error) => (
                    <p key={error} className="text-sm text-destructive">{t(error)}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label htmlFor="price" className="text-sm font-medium leading-none">
                  {t('admin.quote.price')}
                </label>
                <input
                  id="price"
                  inputMode="decimal"
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={t('admin.quote.price_placeholder')}
                  value={responseForm.price}
                  aria-invalid={fieldErrors.price.length > 0}
                  aria-describedby={fieldErrors.price.length > 0 ? 'price-error' : undefined}
                  onChange={(e) => onPriceChange(e.target.value)}
                />
                {fieldErrors.price.length > 0 && (
                  <div id="price-error" className="space-y-1">
                    {fieldErrors.price.map((error) => (
                      <p key={error} className="text-sm text-destructive">{t(error)}</p>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-2">
                <label htmlFor="currency" className="text-sm font-medium leading-none">
                  {t('admin.quote.currency')}
                </label>
                <select
                  id="currency"
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={responseForm.currency}
                  onChange={(e) => setResponseForm({...responseForm, currency: e.target.value})}
                >
                  {SUPPORTED_CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {t(`admin.quote.currency_options.${currency}`, currency)}
                    </option>
                  ))}
                </select>
                {fieldErrors.currency.length > 0 && (
                  <div className="space-y-1">
                    {fieldErrors.currency.map((error) => (
                      <p key={error} className="text-sm text-destructive">{t(error)}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label htmlFor="loading_date" className="text-sm font-medium leading-none">
                  {t('admin.quote.loading_date')}
                  {!responseForm.no_specific_loading_date && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  id="loading_date"
                  type="date"
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={responseForm.loading_date}
                  onChange={(e) => setResponseForm({
                    ...responseForm,
                    loading_date: e.target.value,
                  })}
                  disabled={responseForm.no_specific_loading_date}
                />
                {fieldErrors.loading_date.length > 0 && (
                  <div className="space-y-1">
                    {fieldErrors.loading_date.map((error) => (
                      <p key={error} className="text-sm text-destructive">{t(error)}</p>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-2">
                <label htmlFor="delivery_date" className="text-sm font-medium leading-none">
                  {t('admin.quote.estimated_delivery')}
                  {!responseForm.no_specific_loading_date && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  id="delivery_date"
                  type="date"
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={responseForm.estimated_delivery_date}
                  onChange={(e) => setResponseForm({...responseForm, estimated_delivery_date: e.target.value})}
                  disabled={responseForm.no_specific_loading_date}
                />
                {fieldErrors.estimated_delivery_date.length > 0 && (
                  <div className="space-y-1">
                    {fieldErrors.estimated_delivery_date.map((error) => (
                      <p key={error} className="text-sm text-destructive">{t(error)}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label htmlFor="valid_until" className="text-sm font-medium leading-none">
                  {t('admin.quote.valid_until')}
                </label>
                <input
                  id="valid_until"
                  type="date"
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={responseForm.valid_until}
                  onChange={(e) => setResponseForm({...responseForm, valid_until: e.target.value})}
                />
              </div>

              <div className="grid gap-2">
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    id="no_specific_date"
                    type="checkbox"
                    className="h-4 w-4 rounded border-input text-foreground focus:ring-ring"
                    checked={responseForm.no_specific_loading_date}
                    onChange={(e) => setResponseForm({
                      ...responseForm,
                      no_specific_loading_date: e.target.checked,
                      loading_date: e.target.checked ? '' : responseForm.loading_date,
                      estimated_delivery_date: e.target.checked ? '' : responseForm.estimated_delivery_date,
                    })}
                  />
                  <label htmlFor="no_specific_date" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {t('admin.quote.no_specific_loading_date')}
                  </label>
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <label htmlFor="observations" className="text-sm font-medium leading-none">
                {t('admin.quote.observations')}
              </label>
              <textarea
                id="observations"
                className="flex min-h-[80px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={t('admin.quote.observations_placeholder')}
                value={responseForm.observations}
                onChange={(e) => setResponseForm({...responseForm, observations: e.target.value})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsResponseModalOpen(false);
              setEditingResponseId(null);
              setFieldErrors(createEmptyResponseFieldErrors());
              setFormErrors([]);
            }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={onSubmitResponse} disabled={submittingResponse}>
              {submittingResponse ? t('common.submitting') : t('common.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
